import moment from "moment";

export const DEVICE_MODELS = ["Spandan Pro", "Spandan Neo", "Spandan Ultra", "Spandan Legacy", "Cardio P1"];

// A device is flagged "expiring soon" this many days before the warranty ends.
const WARRANTY_EXPIRING_DAYS = 60;
const IDLE_AFTER_DAYS = 30;

const firstValue = (obj, keys) => {
  for (const key of keys) {
    const val = obj?.[key];
    if (val !== undefined && val !== null && val !== "") return val;
  }
  return undefined;
};

// The list API can send snake_case or camelCase depending on the source, so every
// screen below works off this single shape.
export function normalizeDevice(raw = {}) {
  const purchaseDate = firstValue(raw, [
    "purchaseDate",
    "purchase_date",
    "dateOfPurchase",
    "purchasedAt",
  ]);
  const warrantyMonths =
    Number(firstValue(raw, ["warrantyMonths", "warranty_months", "warrantyPeriod"])) || 0;

  let warrantyExpiry = firstValue(raw, [
    "warrantyExpiry",
    "warranty_expiry",
    "warrantyExpiresAt",
    "warrantyEndDate",
  ]);
  if (!warrantyExpiry && purchaseDate && warrantyMonths) {
    warrantyExpiry = moment(purchaseDate).add(warrantyMonths, "months").toISOString();
  }

  return {
    id: firstValue(raw, ["_id", "id", "deviceId", "device_id"]),
    deviceId: firstValue(raw, ["deviceId", "device_id", "deviceCode"]) || "",
    serialNumber:
      firstValue(raw, ["serialNumber", "serial_number", "serial", "sn"]) || "",
    model: firstValue(raw, ["model", "deviceModel", "model_name"]) || "",
    centreName: firstValue(raw, ["centerName", "centreName", "center_name"]) || "",
    purchaseDate,
    warrantyExpiry,
    warrantyMonths,
    totalTests:
      Number(
        firstValue(raw, ["totalTests", "testCount", "tests_count", "reportsCount"])
      ) || 0,
    lastUsedAt: firstValue(raw, [
      "lastUsedAt",
      "last_used_at",
      "lastTestAt",
      "lastActivityAt",
    ]),
  };
}

export const WARRANTY_STATUS_META = {
  active: {
    label: "Active",
    color: "var(--app-success)",
    bg: "var(--app-success-bg)",
  },
  expiring: {
    label: "Expiring soon",
    color: "var(--app-warning)",
    bg: "var(--app-warning-bg)",
  },
  expired: {
    label: "Expired",
    color: "var(--app-danger)",
    bg: "var(--app-danger-bg)",
  },
  unknown: {
    label: "Not set",
    color: "var(--app-text-muted)",
    bg: "var(--app-surface-2)",
  },
};

export const WARRANTY_STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "expiring", label: "Expiring soon" },
  { value: "expired", label: "Expired" },
];

export function getWarrantyStatus(device) {
  if (!device?.warrantyExpiry) return "unknown";
  const expiry = moment(device.warrantyExpiry);
  if (!expiry.isValid()) return "unknown";

  const days = expiry.startOf("day").diff(moment().startOf("day"), "days");
  if (days < 0) return "expired";
  if (days <= WARRANTY_EXPIRING_DAYS) return "expiring";
  return "active";
}

export function getActivity(device) {
  const lastUsed = device?.lastUsedAt ? moment(device.lastUsedAt) : null;
  const hasLastUsed = lastUsed?.isValid();

  if (!hasLastUsed && !device?.totalTests) {
    return { key: "never", label: "Never used", color: "var(--app-text-muted)" };
  }
  if (!hasLastUsed || moment().diff(lastUsed, "days") > IDLE_AFTER_DAYS) {
    return { key: "idle", label: "Idle", color: "var(--app-danger)" };
  }
  return { key: "active", label: "Active", color: "var(--app-success)" };
}

export function formatDate(value) {
  if (!value) return "—";
  const date = moment(value);
  return date.isValid() ? date.format("DD MMM YYYY") : "—";
}

export function formatLastUsed(value) {
  if (!value) return null;
  const date = moment(value);
  if (!date.isValid()) return null;
  if (date.isSame(moment(), "day")) return "Today";
  if (date.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return date.fromNow();
}

export function buildActivityDetail(device) {
  const tests = `${device.totalTests} ${device.totalTests === 1 ? "test" : "tests"}`;
  const lastUsed = formatLastUsed(device.lastUsedAt);
  return lastUsed ? `${tests} · last used ${lastUsed}` : tests;
}
